import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { ArrowLeft, ArrowRight, Paperclip, Clock, BookOpen, CheckCircle2, Hourglass, XCircle, Pencil } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";

export const Route = createFileRoute("/teacher/grading")({
  component: GradingPage,
  validateSearch: (search: Record<string, unknown>) => ({
    filter: (search.filter as string) || undefined,
  }),
});

const QUICK = ["Tốt!", "Khá", "Cần cải thiện", "Thiếu ý", "Sai công thức", "Trình bày tốt"];

type SubRow = {
  id: string;
  assignment_id: string;
  student_id: string;
  content: string | null;
  submitted_at: string;
  score: number | null;
  feedback: string | null;
  file_url: string | null;
  file_name: string | null;
  studentName: string;
};

function GradingPage() {
  const { filter } = Route.useSearch();
  const { user } = useAuth();
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const isDueSoon = filter === "due-soon";

  const [selectedClass, setSelectedClass] = React.useState("");
  const [selectedSession, setSelectedSession] = React.useState("");
  const [selectedSub, setSelectedSub] = React.useState<SubRow | null>(null);
  const [score, setScore] = React.useState<number[]>([8]);
  const [feedback, setFeedback] = React.useState("");

  const classesQ = useQuery({
    queryKey: ["teacher-classes", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("classes").select("id, name").eq("teacher_id", user!.id).order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const sessionsQ = useQuery({
    queryKey: ["grading-sessions", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("class_sessions").select("id, starts_at, topic")
        .eq("class_id", selectedClass).order("starts_at");
      if (error) throw error;
      return (data ?? []).map((s, i) => ({ ...s, sessionNumber: i + 1 }));
    },
  });

  const assignmentsQ = useQuery({
    queryKey: ["grading-assignments", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assignments").select("id, title, session_id, due_at, max_score")
        .eq("class_id", selectedClass);
      if (error) throw error;
      return data ?? [];
    },
  });

  const enrolledQ = useQuery({
    queryKey: ["grading-enrolled", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { data } = await supabase
        .from("enrollments").select("student_id")
        .eq("class_id", selectedClass).eq("is_active", true);
      const ids = (data ?? []).map(e => e.student_id);
      if (!ids.length) return [];
      const { data: profs } = await supabase.from("profiles").select("id, display_name, email").in("id", ids);
      return (profs ?? []).map(p => ({ id: p.id, name: p.display_name ?? p.email?.split("@")[0] ?? "—" }));
    },
  });
  const enrolledCount = enrolledQ.data?.length ?? 0;

  const submissionsQ = useQuery({
    queryKey: ["grading-submissions", selectedClass],
    enabled: !!selectedClass && !!assignmentsQ.data,
    queryFn: async () => {
      const ids = (assignmentsQ.data ?? []).map(a => a.id);
      if (!ids.length) return [] as SubRow[];
      const { data, error } = await supabase
        .from("submissions")
        .select("id, assignment_id, student_id, content, submitted_at, score, feedback, file_url, file_name")
        .in("assignment_id", ids)
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      const studentIds = Array.from(new Set((data ?? []).map(s => s.student_id)));
      const { data: profs } = studentIds.length
        ? await supabase.from("profiles").select("id, display_name, email").in("id", studentIds)
        : { data: [] };
      const profMap = Object.fromEntries((profs ?? []).map(p => [p.id, p.display_name ?? p.email?.split("@")[0] ?? "—"]));
      return (data ?? []).map(s => ({ ...s, studentName: profMap[s.student_id] ?? "—" })) as SubRow[];
    },
  });

  const sessionsForGrading = React.useMemo(() => {
    const sessions = sessionsQ.data ?? [];
    const assigns = assignmentsQ.data ?? [];
    const subs = submissionsQ.data ?? [];

    return sessions.map(s => {
      const sessionAssigns = assigns.filter(a => a.session_id === s.id);
      const sessionAssignIds = new Set(sessionAssigns.map(a => a.id));
      const sessionSubs = subs.filter(sub => sessionAssignIds.has(sub.assignment_id));

      const gradedCount = sessionSubs.filter(sub => sub.score !== null).length;
      const ungradedCount = sessionSubs.filter(sub => sub.score === null).length;
      const submittedStudentIds = new Set(sessionSubs.map(sub => sub.student_id));
      const notSubmittedCount = Math.max(0, (enrolledCount - submittedStudentIds.size) * sessionAssigns.length);

      const now = new Date();
      const in7 = new Date(now); in7.setDate(in7.getDate() + 7);
      const hasDueSoon = sessionAssigns.some(a => {
        if (!a.due_at) return false;
        const d = new Date(a.due_at);
        return d >= now && d <= in7;
      });

      return { ...s, gradedCount, ungradedCount, notSubmittedCount, hasDueSoon, hasAssignments: sessionAssigns.length > 0 };
    }).filter(s => (isDueSoon ? s.hasDueSoon : true));
  }, [sessionsQ.data, assignmentsQ.data, submissionsQ.data, isDueSoon, enrolledCount]);

  // List for selected session
  const sessionAssignIds = React.useMemo(() => {
    const a = (assignmentsQ.data ?? []).filter(x => x.session_id === selectedSession).map(x => x.id);
    return new Set(a);
  }, [assignmentsQ.data, selectedSession]);

  const submittedList = React.useMemo(() => {
    return (submissionsQ.data ?? []).filter(s => sessionAssignIds.has(s.assignment_id));
  }, [submissionsQ.data, sessionAssignIds]);

  const notSubmittedList = React.useMemo(() => {
    const submittedIds = new Set(submittedList.map(s => s.student_id));
    return (enrolledQ.data ?? []).filter(s => !submittedIds.has(s.id));
  }, [enrolledQ.data, submittedList]);

  // Sync editor state when active changes
  React.useEffect(() => {
    if (selectedSub) {
      setScore([selectedSub.score ?? 8]);
      setFeedback(selectedSub.feedback ?? "");
    }
  }, [selectedSub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset session selection if class changes
  React.useEffect(() => { setSelectedSession(""); setSelectedSub(null); }, [selectedClass]);
  React.useEffect(() => { setSelectedSub(null); }, [selectedSession]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!selectedSub) throw new Error("Chưa chọn bài");
      const { error } = await supabase.from("submissions").update({
        score: score[0],
        feedback: feedback || null,
        graded_at: new Date().toISOString(),
        graded_by: user?.id,
      }).eq("id", selectedSub.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(`Đã lưu điểm ${score[0]}`);
      qc.invalidateQueries({ queryKey: ["grading-submissions", selectedClass] });
      // Auto-select next ungraded
      const remaining = submittedList.filter(s => s.id !== selectedSub?.id && s.score === null);
      setSelectedSub(remaining[0] ?? null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const currentSession = sessionsQ.data?.find(s => s.id === selectedSession);

  return (
    <PortalLayout portal="teacher" title="Chấm điểm" breadcrumb={[{ label: "Giáo viên", to: "/teacher" }, { label: "Chấm điểm" }]}>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={selectedClass} onValueChange={setSelectedClass}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Chọn lớp..." /></SelectTrigger>
          <SelectContent>
            {(classesQ.data ?? []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {isDueSoon && <Badge variant="destructive" className="text-xs"><Clock className="size-3 mr-1" /> Bài sắp hết hạn</Badge>}
      </div>

      {!selectedClass && (
        <div className="rounded-2xl border-2 border-dashed p-12 text-center text-sm text-muted-foreground">
          Hãy chọn lớp để xem các buổi cần chấm.
        </div>
      )}

      {/* Danh sách buổi */}
      {selectedClass && !selectedSession && (
        <div className="space-y-2">
          {sessionsForGrading.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed p-12 text-center text-sm text-muted-foreground">
              {isDueSoon ? "Không có buổi nào sắp đến hạn." : "Lớp này chưa có buổi học nào."}
            </div>
          ) : sessionsForGrading.map(s => (
            <motion.div key={s.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -2 }}
              onClick={() => setSelectedSession(s.id)}
              className={`cursor-pointer rounded-xl border p-4 hover:shadow-md transition-all ${
                s.ungradedCount > 0 ? "border-amber-200 bg-amber-50/30" : ""
              }`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium">Buổi {s.sessionNumber}</span>
                  {s.topic && <span className="text-xs text-muted-foreground truncate flex items-center gap-1"><BookOpen className="size-3" /> {s.topic}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  {!s.hasAssignments && <Badge variant="outline" className="text-muted-foreground">Chưa có BT</Badge>}
                  {s.gradedCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="size-3 mr-1" /> {s.gradedCount} đã chấm</Badge>}
                  {s.ungradedCount > 0 && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Hourglass className="size-3 mr-1" /> {s.ungradedCount} chưa chấm</Badge>}
                  {s.notSubmittedCount > 0 && <Badge variant="outline" className="text-muted-foreground"><XCircle className="size-3 mr-1" /> {s.notSubmittedCount} chưa nộp</Badge>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* 3 cột */}
      {selectedClass && selectedSession && (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setSelectedSession("")} className="mb-4">
            <ArrowLeft className="size-4" /> Quay lại
          </Button>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Cột trái */}
            <div className="bg-card border rounded-xl p-4 max-h-[70vh] overflow-y-auto">
              <h4 className="font-semibold text-sm mb-3">
                Buổi {currentSession?.sessionNumber}
                {currentSession?.topic && <span className="ml-2 text-xs font-normal text-muted-foreground">{currentSession.topic}</span>}
              </h4>

              <div className="mb-3">
                <p className="text-xs text-muted-foreground mb-2">Đã nộp ({submittedList.length})</p>
                <div className="space-y-1">
                  {submittedList.length === 0 && (
                    <p className="text-xs text-muted-foreground italic px-2">Chưa có bài nộp.</p>
                  )}
                  {submittedList.map(s => (
                    <button key={s.id} onClick={() => setSelectedSub(s)}
                      className={`w-full text-left text-sm p-2.5 rounded-lg hover:bg-muted/40 transition-colors ${
                        selectedSub?.id === s.id ? "bg-[var(--portal-soft)] ring-1 ring-[var(--portal)]/20" : ""
                      }`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{s.studentName}</span>
                        {s.score !== null
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">{s.score}đ</Badge>
                          : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Chưa chấm</Badge>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {notSubmittedList.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Chưa nộp ({notSubmittedList.length})</p>
                  <div className="space-y-1">
                    {notSubmittedList.map(s => (
                      <div key={s.id} className="text-sm p-2.5 rounded-lg text-muted-foreground opacity-50">
                        {s.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Cột giữa */}
            <div className="bg-card border rounded-xl p-4">
              {selectedSub ? (
                <div>
                  <h4 className="font-semibold text-sm mb-3">{selectedSub.studentName}</h4>
                  {selectedSub.content && (
                    <div className="text-sm bg-muted/40 rounded-lg p-3 mb-3 whitespace-pre-wrap">{selectedSub.content}</div>
                  )}
                  {selectedSub.file_url && (
                    <a href={selectedSub.file_url} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3">
                      <Paperclip className="size-3.5" /> {selectedSub.file_name ?? "Xem file"}
                    </a>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Nộp lúc: {format(new Date(selectedSub.submitted_at), "HH:mm dd/MM/yyyy", { locale: vi })}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-16">
                  <div className="text-2xl mb-2">📄</div>
                  Chọn bài nộp bên trái để xem
                </div>
              )}
            </div>

            {/* Cột phải: Chấm điểm */}
            <div className="bg-card border rounded-xl p-4">
              {selectedSub ? (
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm">Chấm điểm</h4>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Điểm số</div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-display text-5xl font-semibold" style={{ color: "var(--portal)" }}>{score[0]}</span>
                      <span className="text-muted-foreground">/ 10</span>
                    </div>
                    <Slider value={score} onValueChange={setScore} min={0} max={10} step={0.5} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground block mb-2">Nhận xét</label>
                    <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Nhập nhận xét..." rows={4} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Feedback nhanh</div>
                    <div className="flex flex-wrap gap-1.5">
                      {QUICK.map(q => (
                        <button key={q} type="button"
                          onClick={() => setFeedback(f => f ? f + " " + q : q)}
                          className="px-2.5 py-1 text-xs rounded-full border hover:bg-muted transition-colors">{q}</button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={() => saveMut.mutate()}
                    disabled={!canMutate || saveMut.isPending}
                    className="w-full gap-2"
                    style={{ background: "var(--portal)", color: "white" }}>
                    Lưu & Tiếp <ArrowRight className="size-4" />
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-16">
                  <Pencil className="size-6 mx-auto mb-2 text-muted-foreground" />
                  Chọn bài nộp để chấm
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
